/**
 * SongNotesService — per-note document lifecycle for the `songNotes` collection.
 *
 * Slice N1b. The collection, its rules and its two composite indexes shipped in
 * N1a and have been live and empty since 2026-07-09; this is the first reader and
 * writer. Entity shape and semantics:
 *   docs/handoffs/song-notes-entity-slice-n1a-code-handoff-2026-07-09.md §2
 *
 * WHAT CHANGED FROM THE OLD MODEL, and why it matters:
 * Notes used to live as an array on `lessons/{lessonId}.songNotes`, re-saved whole
 * on a 2s debounce. Each lesson owned its own copy and note ids were minted
 * `Date.now()` per lesson, so "vowels" in lesson A and "vowels" in lesson B were
 * unrelated objects. The cross-teacher continuity the studio runs on could not
 * happen — a missing entity, not a missing button. One document per note fixes
 * that, and structurally eliminates the whole-array-autosave bug class that
 * produced the repertoire session inflation.
 *
 * A note hangs off (student × song). The lesson is PROVENANCE, not an access path:
 * `originLessonId` records where a note came from and is never a route back to it.
 *
 * All writes here are staff-only by rule (songNotes allows employee/admin with
 * hasPerm('viewStudents')). Students and parents never write, ever.
 */

import {
	collection,
	doc,
	addDoc,
	updateDoc,
	deleteDoc,
	getDocs,
	query,
	where,
	orderBy,
	limit,
	serverTimestamp,
	arrayUnion,
	Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
	SONG_NOTE_STATUS,
	DELIVERED_STATUSES,
	isLegalTransition,
	buildHistoryEntry,
} from './songNotesModel';

const COLLECTION_NAME = 'songNotes';

/** Open work = everything not yet landed. Matches index 2's `status` filter. */
const OPEN_STATUSES = [SONG_NOTE_STATUS.CAPTURED, SONG_NOTE_STATUS.FOCUS];

// A note whose song was removed from the repertoire (removedAt, admin-only;
// repertoireRemovalModel.js) is hidden from EVERY read here, so the lesson
// board, carry-forward, the profile and the family Songs page all agree. The
// restore path queries removed notes itself.
const mapSnapshot = (snapshot) =>
	snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).filter((n) => !n.removedAt);

/**
 * Every note for one (student × song), newest first.
 *
 * Queried ASCENDING on purpose. The deployed composite index is
 * (studentId ASC, songId ASC, createdAt ASC), and an orderBy in the OTHER
 * direction is NOT served by it — same fields, same order, and prod returns
 * FAILED_PRECONDITION while the emulator happily serves either (the index
 * DIRECTION trap, hit live 2026-08-05; this exact query 400'd for as long as
 * anything called it). Sorting finishes client-side with a reverse(), which
 * costs nothing at per-song note counts and saves an index deploy entirely.
 * Do NOT "simplify" this back to orderBy('createdAt', 'desc') without
 * shipping a DESC index in the same release.
 */
export const fetchNotesForSong = async (studentId, songId) => {
	if (!studentId || !songId) return [];
	const q = query(
		collection(db, COLLECTION_NAME),
		where('studentId', '==', studentId),
		where('songId', '==', songId),
		orderBy('createdAt', 'asc')
	);
	return mapSnapshot(await getDocs(q)).reverse();
};

/**
 * Every note for a student across all songs. Used to hydrate the song tabs in one
 * read rather than one read per song.
 */
export const fetchNotesForStudent = async (studentId) => {
	if (!studentId) return [];
	const q = query(
		collection(db, COLLECTION_NAME),
		where('studentId', '==', studentId),
		orderBy('createdAt', 'desc')
	);
	return mapSnapshot(await getDocs(q));
};

/**
 * The carry-forward read: this student's outstanding work from every prior lesson
 * and every teacher. Served by index 2: studentId ASC, status ASC, statusChangedAt DESC.
 *
 * This is the query that makes the three-teacher rotation work. Without it, a
 * teacher opening a lesson sees nothing the previous two teachers noticed.
 */
export const fetchCarryForwardNotes = async (studentId, max = 100) => {
	if (!studentId) return [];
	const q = query(
		collection(db, COLLECTION_NAME),
		where('studentId', '==', studentId),
		where('status', 'in', OPEN_STATUSES),
		orderBy('statusChangedAt', 'desc'),
		limit(max)
	);
	return mapSnapshot(await getDocs(q));
};

// fetchDeliveredNotesForSong was DELETED here (2026-08-06, Anthony's call on
// the C decision): it had zero callers ever, and its query shape
// (studentId ==, songId ==, status in, orderBy createdAt DESC) needs a
// composite index that was never deployed — so it 400'd in prod from the day
// it was written while the emulator served it happily. If a customer-facing
// per-song view ever needs it, rebuild it WITH its index in the same release,
// or filter in memory off fetchDeliveredNotesForStudent — which is what the
// customer Songs page does, and why it needs no extra index.

/**
 * The family-facing read the customer Songs page runs — every DELIVERED note for
 * one student, across every song, newest delivery first.
 *
 * ONE STUDENT PER CALL, DELIBERATELY, and this is not an oversight to optimise
 * away into a familyId query. Family membership is resolved by the rule through
 * a get() on students/{studentId} and is never denormalized onto the note (a
 * copied familyId goes stale the day a family merges — firestore.rules says so
 * above both this collection and `documents`). Constraining to a single
 * studentId is therefore what makes the rule satisfiable AND what collapses its
 * get() to one cached document access no matter how many notes return. A parent
 * with two children issues two of these; N is realistically ≤3.
 *
 * RULES ARE NOT FILTERS: both `studentId` and the DELIVERED_STATUSES predicate
 * are required by the rule, so dropping either constraint does not widen the
 * result — it denies the query whole and the page renders empty.
 *
 * Served by the already-deployed index 2 (studentId ASC, status ASC,
 * statusChangedAt DESC). Do NOT change this orderBy to createdAt without adding
 * a composite index in the same deploy: the emulator serves either one and only
 * production tells you the difference.
 */
export const fetchDeliveredNotesForStudent = async (studentId, max = 200) => {
	if (!studentId) return [];
	const q = query(
		collection(db, COLLECTION_NAME),
		where('studentId', '==', studentId),
		where('status', 'in', DELIVERED_STATUSES),
		orderBy('statusChangedAt', 'desc'),
		limit(max)
	);
	return mapSnapshot(await getDocs(q));
};

/**
 * Create a note. Always born `captured` — teacher-only, undelivered.
 *
 * Fast-jot is the shape to preserve: the existing surface is Enter-to-add from a
 * single input with no status picker at entry, and that is correct. A teacher
 * mid-lesson is not choosing a lifecycle state, they are writing something down
 * before they forget it. Delivery is a separate, deliberate act.
 */
export const createSongNote = async ({
	studentId,
	songId,
	songName = '',
	text,
	originLessonId = null,
	createdBy = null,
	sectionId = null, // song section the note belongs to (2026-09-25); null = the whole song
	sectionLabel = null, // denormalised so any reader can show "Chorus 2" without the repertoire row
}) => {
	const trimmed = (text || '').trim();
	if (!studentId || !songId || !trimmed) {
		throw new Error('createSongNote requires studentId, songId and non-empty text');
	}
	// A board-transient id can NEVER be a repertoire doc id, so a note persisted
	// against one is born orphaned — no tab to bucket onto, visible only in the
	// carry-forward rail, permanently undeliverable. That happened silently on
	// 2026-08-05 (three "Crazy for you" notes on one dead `local-` id). Callers
	// must resolve a real repertoire row first (see useSongNotes.flushLocal);
	// this guard turns the next half-done id swap into a loud failure instead.
	if (/^(local|pending)-/.test(songId)) {
		throw new Error(
			`createSongNote refuses transient songId "${songId}" — resolve a repertoire row first`
		);
	}

	const now = Timestamp.now();
	const status = SONG_NOTE_STATUS.CAPTURED;

	// serverTimestamp() cannot appear inside an array element — Firestore rejects
	// the write. history[] entries carry a client Timestamp; only the top-level
	// fields get serverTimestamp().
	const history = [
		buildHistoryEntry({ status, at: now, lessonId: originLessonId, byTeacher: createdBy }),
	];

	const payload = {
		studentId,
		songId,
		songName: songName || '',
		songNameLower: (songName || '').trim().toLowerCase(),
		text: trimmed,
		status,
		statusChangedAt: serverTimestamp(),
		history,
		originLessonId: originLessonId || null,
		createdBy: createdBy || null,
		sectionId: sectionId || null,
		sectionLabel: sectionId ? sectionLabel || null : null,
		createdAt: serverTimestamp(),
		updatedAt: serverTimestamp(),
	};

	const ref = await addDoc(collection(db, COLLECTION_NAME), payload);
	// Echo back a locally-usable row: serverTimestamp() resolves to null on the
	// client until the write round-trips, and the UI sorts on statusChangedAt.
	return { id: ref.id, ...payload, statusChangedAt: now, createdAt: now, updatedAt: now };
};

/** Edit note text. Debounce at the call site — one document, one small write. */
export const updateSongNoteText = async (noteId, text) => {
	const trimmed = (text || '').trim();
	if (!noteId || !trimmed) return;
	await updateDoc(doc(db, COLLECTION_NAME, noteId), {
		text: trimmed,
		updatedAt: serverTimestamp(),
	});
};

/** Move a note to another song section (or to none). Visibility is untouched. */
export const updateSongNoteSection = async (noteId, { sectionId = null, sectionLabel = null } = {}) => {
	if (!noteId) return;
	await updateDoc(doc(db, COLLECTION_NAME, noteId), {
		sectionId: sectionId || null,
		sectionLabel: sectionId ? sectionLabel || null : null,
		updatedAt: serverTimestamp(),
	});
};

/**
 * Move a note between states. One write: `status` + `statusChangedAt` +
 * an appended history entry.
 *
 * Rejects illegal transitions rather than silently accepting them — in particular
 * anything returning to `captured`. A delivered note has been seen by the family;
 * letting it slip back behind the wall would make it vanish from their view with
 * no trace. To remove a delivered note, delete it.
 *
 * `statusChangedAt` is derived-on-write from this transition, never accumulated.
 * It is not a counter — do not add one. "How many lessons did this take to land"
 * is answerable from history[] for free.
 */
export const setSongNoteStatus = async (noteId, { from, to, lessonId = null, byTeacher = null }) => {
	if (!noteId) throw new Error('setSongNoteStatus requires a noteId');
	if (!isLegalTransition(from, to)) {
		throw new Error(`Illegal Song Note transition: ${from} → ${to}`);
	}

	const at = Timestamp.now();
	await updateDoc(doc(db, COLLECTION_NAME, noteId), {
		status: to,
		statusChangedAt: serverTimestamp(),
		history: arrayUnion(buildHistoryEntry({ status: to, at, lessonId, byTeacher })),
		updatedAt: serverTimestamp(),
	});
	return { at };
};

/**
 * Delete a note outright.
 *
 * Deleting a `captured` note is free — nobody ever saw it. Deleting a delivered
 * note is a real removal from a family's view, so the UI confirms that case.
 */
export const deleteSongNote = async (noteId) => {
	if (!noteId) return;
	await deleteDoc(doc(db, COLLECTION_NAME, noteId));
};

const SongNotesService = {
	fetchNotesForSong,
	fetchNotesForStudent,
	fetchCarryForwardNotes,
	fetchDeliveredNotesForStudent,
	createSongNote,
	updateSongNoteText,
	updateSongNoteSection,
	setSongNoteStatus,
	deleteSongNote,
};

export default SongNotesService;
